/*global mzFunc */
import React, { useState, useEffect } from 'react'
import FormStepsNav from '../../2_Molecules/FormStepsNav/FormStepsNav'
import FindDealerStep from '../../4_Templates/FindDealerStep/FindDealerStep'
import DateAndTimeStep from '../../4_Templates/DateAndTimeStep/DateAndTimeStep'
import ContactDetailsStep from '../../4_Templates/ContactDetailsStep/ContactDetailsStep'
import SelectVehicleStep from '../../4_Templates/SelectVehicleStep/SelectVehicleStep'
import { languageController } from '../../../controllers/language/languageController'
import StepWrapper from '../../2_Molecules/StepWrapper/StepWrapper'
import ConfirmationStep from '../../4_Templates/ConfirmationStep/ConfirmationStep'
import { convertLangCodeToSingleLetter, displayOutsideARBlock, displayOutsideComponents, hideOutsideComponents, pushToGTMDataLayer, scrollToTop, handleAnalyticsTrackingCodes } from '../../../utils/helpers'
import pidController from '../../../controllers/pidController'
import { getEndpointUrl } from '../../../utils/endPoints'
import { Location } from '../../../types'

// Vehicle data interface
export interface VehicleData {
  id?: string;
  isEV?: boolean;
  title: string;
  model: string;
  modelYear: string;
  msrp: number;
  year?: string;
  description?: string;
  visual?: {
    static_content_base_path?: string;
    jelly_path?: string;
    jelly?: string;
    jelly_hover?: string;
    interiors?: any[];
    exteriors?: any;
    [key: string]: any;
  };
  modelCode?: string;
  modelOption?: string;
  isModelPreOrder?: boolean;
  [key: string]: any;
}

// Dealer data interface
export interface DealerDataItem {
  id?: string;
  dealer_code?: string;
  name: string;
  address?: string;
  address_line_1?: string;
  city?: string;
  province?: string;
  postalCode?: string;
  post_code?: string;
  phone?: string;
  phone_number?: string;
  email?: string;
  website?: string;
  distance?: number;
  latitude?: number;
  longitude?: number;
  geolocation?: {
    distance: number;
    latitude: number;
    longitude: number;
  };
  [key: string]: any;
}

export interface DealerData {
  dealer: DealerDataItem;
  userPosition?: Location;
  [key: string]: any;
}

// Date and time data interface
export interface DateAndTimeData {
  date: Date;
  time: string;
  [key: string]: any;
}

// Contact details data interface
export interface ContactDetailsData {
  firstname: string;
  lastname: string;
  email: string;
  postalcode: string;
  optin?: string;
  [key: string]: any;
}

// Form data state interface
export interface FormData {
  vehicleData?: VehicleData;
  dealerData?: DealerData;
  dateAndTime?: DateAndTimeData;
  contactDetails?: ContactDetailsData;
}

// Form extra params interface
export interface FormExtraParams {
  isTopSectionHidden?: string;
  source?: string;
  language?: string;
  lastStepBottomContents?: string;
  initStepTopContents?: string;
  onlyEVDealers?: string;
  isEmbeddedOnBnP?: string;
  introductionText?: string;
  formDisclaimer?: string;
  modelCode?: string;
  modelOption?: string;
  dealerId?: string;
  [key: string]: any;
}

// Pre-order item interface for window.preOrder
interface PreOrderItem {
  modelCode: string;
  modelOption: string;
  [key: string]: any;
}

// Extend window interface for preOrder and bnp_retailer_mode
declare global {
  interface Window {
    preOrder?: PreOrderItem[];
    bnp_retailer_mode?: boolean;
    summaryCalculator?: any;  // Matches SendBuildToDealer declaration
  }
}

// Component props interface
export interface BookTestDriveProps {
  formExtraParams: FormExtraParams;
  preDefinedVehicleData?: VehicleData | null;
  preDefinedDealerData?: DealerData | null;
  preDefinedUserLocation?: Location | null;
  initStep: number;
  containerRef: React.RefObject<HTMLDivElement>;
}

const BookTestDrive: React.FC<BookTestDriveProps> = ({
  formExtraParams,
  preDefinedVehicleData,
  preDefinedDealerData,
  preDefinedUserLocation,
  initStep,
  containerRef
}) => {
  const { isTopSectionHidden, source, language, lastStepBottomContents, initStepTopContents, onlyEVDealers, isEmbeddedOnBnP, introductionText, formDisclaimer, modelCode, modelOption } = formExtraParams
  const maxStepNum = 4;

  // Convert boolean string to bool type.
  const isTopSectionHiddenFormatted = (isTopSectionHidden ? isTopSectionHidden.toLowerCase() === 'true' : false);
  const onlyEVDealersFormatted = (onlyEVDealers ? onlyEVDealers.toLowerCase() === 'true' : false);
  const isEmbeddedOnBnPFormatted = (isEmbeddedOnBnP ? isEmbeddedOnBnP.toLowerCase() === 'true' : false);

  const [currentStep, setCurrentStep] = useState<number>(initStep)
  const [currentProgress, setCurrentProgress] = useState<number>(initStep)

  const [dealerID, setDealerID] = useState<string | undefined>(formExtraParams.dealerId);

  const isSelectionPreOrder = (): boolean => {
    let isPreOrder = false;

    if (window.preOrder) {
      window.preOrder.forEach((item) => {
        if (item.modelCode === modelCode && item.modelOption === modelOption) {
          isPreOrder = true;
        }
      });
    }

    return isPreOrder;
  }

  if (preDefinedVehicleData) {
    preDefinedVehicleData.modelCode = modelCode ? modelCode : '';
    preDefinedVehicleData.modelOption = modelOption ? modelOption : '';
    preDefinedVehicleData.isModelPreOrder = isSelectionPreOrder();
  }

  const [data, setData] = useState<FormData>({
    vehicleData: preDefinedVehicleData || undefined,
    dealerData: preDefinedDealerData || undefined,
    dateAndTime: undefined,
    contactDetails: undefined
  })

  const [returnToStart, setReturnToStart] = useState<boolean>(false);

  // Display top contents on the init step.
  useEffect(() => {
    if (initStep === 0) {
      displayOutsideComponents(initStepTopContents)
    }

    // HOTFIX FOR CASE LIKE https://preprod.mazda.ca/en/shopping/Request-a-test-drive/?model=ZXSL86%7cAA00
    if (initStep === 1) {
      hideOutsideComponents(initStepTopContents);

      setReturnToStart(true);
    }

    // Add the form-related vars to GTM dataLayer.
    pushToGTMDataLayer({ form: 'Book a Test Drive' });
    pushToGTMDataLayer({ formid: 'BaTD' });
    // If initStep is later than 0, handle analytics code for each step.
    if (initStep > 0) {
      for (let step = 0; step < initStep; step++) {
        setupTrackingCodes(data, step);
      }
    } else {
      setReturnToStart(true);
    }

    if (window.bnp_retailer_mode) {
      // Set the polling until we have a dealer vars with the information set as we should always have the dealer vars information
      const intervalRef = setInterval(() => {
        const dealerVars = window.summaryCalculator?.getDealerVars();

        if (dealerVars) {
          console.log(dealerVars);
          setDealerID(dealerVars.dealerId);

          clearInterval(intervalRef);
        }
      }, 200);
    }
  }, [])

  useEffect(() => {
    const heroContainer = document.querySelector('.form-hero-container');
    const heroEl = document.getElementById('mz-hero');
    if (!heroContainer || !heroEl || heroEl.parentElement === heroContainer) return;

    const originalParentEl = heroEl.parentElement;
    heroEl.classList.remove('visually-hidden');
    heroContainer.appendChild(heroEl);

    // Restore the hero block when the form is unmounted.
    return () => {
      if (originalParentEl) originalParentEl.appendChild(heroEl);
      heroEl.classList.add('visually-hidden');
    };
  }, [])

  useEffect(() => {
    if(typeof mzFunc === 'object') {
      console.log(mzFunc);
      mzFunc.updateOffsets();
    }
    
    // Update 'formstep' dataLayer var when currentStep is changed
    pushToGTMDataLayer({ formstep: `Step${currentStep}` });
  }, [currentStep, returnToStart])

  useEffect(() => {
    console.log('BookTestDrive - forced a redraw with', dealerID);
  }, [dealerID, data])

  const handleNextStep = (newProps: Partial<FormData>): void => {
    if (currentStep === maxStepNum) return;

    const nextStep = currentStep + 1;

    // Manage visibility of components outside of the react app.
    if (nextStep === 1) {
      // Hide BaTD Hero and Intro section
      hideOutsideComponents(initStepTopContents)
    } else if (nextStep === maxStepNum) {
      // Display BaTD related bottom contents on confirmation step.
      displayOutsideComponents(lastStepBottomContents)
      // Display AR Block of the selected model
      if (data.vehicleData?.model) {
        displayOutsideARBlock(data.vehicleData.model)
      }
    }

    setCurrentStep(nextStep)

    setData({ ...data, ...newProps })
    setupTrackingCodes({ ...data, ...newProps }, currentStep)

    setCurrentProgress(prevState => nextStep > prevState ? nextStep : prevState)

    // Scroll to the top of the screen.
    if (!isEmbeddedOnBnPFormatted) {
      scrollToTop();
    }
  }

  const handlePreviousStep = (): void => {
    if (currentStep === initStep) return;

    const previousStep = currentStep - 1;

    // Manage visibility of components outside of the react app.
    /*if (nextStep === 1) {
      // Hide BaTD Hero and Intro section
      hideOutsideComponents(initStepTopContents)
    } else if (nextStep === maxStepNum) {
      // Display BaTD related bottom contents on confirmation step.
      displayOutsideComponents(lastStepBottomContents)
      // Display AR Block of the selected model
      displayOutsideARBlock(data.vehicleData?.model)
    }*/
    // Remove the selected data
    const newProps: Partial<FormData> = { dealerData: undefined }

    const newData = { ...data, ...newProps }
    setData(newData)

    setCurrentStep(previousStep)

    setupTrackingCodes({ ...data, ...newProps }, currentStep)

    setCurrentProgress(prevState => previousStep < prevState ? previousStep : prevState)

    // Scroll to the top of the screen.
    if (!isEmbeddedOnBnPFormatted) {
      scrollToTop();
    }
  }

  // Refresh data and go back to init step.
  const refreshSteps = (): void => {
    setReturnToStart(true)
    setCurrentStep(0)
    setCurrentProgress(0)
    setData({})
    displayOutsideComponents(initStepTopContents)
    scrollToTop()
  }

  // Handle adding analytics tracking attributes.
  const setupTrackingCodes = async (data: FormData, completedStep: number): Promise<void> => {
    try {
      // Set vehicle-related tracking attributes
      if (completedStep === 0 && data.vehicleData && !isEmbeddedOnBnPFormatted) {
        const trackData = { modelCarlineName: data.vehicleData.model, modelYear: Number(data.vehicleData.modelYear), msrp: data.vehicleData.msrp };
        handleAnalyticsTrackingCodes(trackData, 'Vehicle Select');
      }
      // Set dealer-related tracking attributes
      if (completedStep === 1 && data.dealerData) {
        const trackData = { dealerName: data.dealerData.dealer?.name };
        handleAnalyticsTrackingCodes(trackData, 'Dealer Select');
      }
      // Set user-related tracking attributes
      if (completedStep === 3 && data.contactDetails) {
        const trackData = { email: data.contactDetails.email, firstname: data.contactDetails.firstname, lastname: data.contactDetails.lastname, postalcode: data.contactDetails.postalcode, optin: data.contactDetails.optin === 'on' ? 'optin' : 'optout' };
        handleAnalyticsTrackingCodes(trackData, 'Contact Details Submit');
      }
    } catch (err) {
      console.log(err)
    }
  }

  return (
    <>
      {!isTopSectionHiddenFormatted && (
        <FormStepsNav
          currentStep={currentStep}
          currentProgress={currentProgress}
          setCurrentStep={setCurrentStep}
          refreshSteps={refreshSteps}
          isActive={(currentStep > 0 && currentStep < maxStepNum)}
          refreshText={languageController.getCopy('global', 'vehicles') || ''}
          navList={[
            {
              step: 1,
              label: languageController.getCopy('global', 'selectDealerNav') || '',
            },
            {
              step: 2,
              label: languageController.getCopy('global', 'selectDateAndTimeNav') || '',
            },
            {
              step: 3,
              label: languageController.getCopy('global', 'contactDetailsNav') || '',
            },
          ]}
        />
      )}
      <div
        className={`mz-book-test-drive ${isEmbeddedOnBnPFormatted ? 'mz-book-test-drive--bnp' : ''}`}
        data-current-step={currentStep}
      >
        <div className={`mz-book-test-drive__steps ${isTopSectionHiddenFormatted ? 'mz-book-test-drive__steps--no-steps-nav' : ''}`}>
          {/* Step 0: Select a vehicle */}
          <StepWrapper
            className={`mz-book-test-drive__select-vehicle`}
            stepNum={0}
            currentStep={currentStep}
            currentProgress={currentProgress}
          >
            <div className="form-hero-container" />

            <SelectVehicleStep
              title={languageController.getCopy('global', 'selectVehicle') || ''}
              handleNextStep={(data) => handleNextStep(data as Partial<FormData>)}
              introductionText={introductionText}
            />
          </StepWrapper>
          {/* Step 1: Select a dealer */}
          <StepWrapper
            className={`mz-book-test-drive__select-dealer`}
            stepNum={1}
            currentStep={currentStep}
            currentProgress={currentProgress}
          >
            <FindDealerStep
              handleNextStep={handleNextStep}
              vehicleData={data.vehicleData as any}
              title={languageController.getCopy('testDrive', 'step1Title') || ''}
              subTitle={languageController.getCopy('global', 'findDealerSub') || ''}
              isTopSectionHidden={isTopSectionHiddenFormatted}
              isEmbeddedOnBnP={isEmbeddedOnBnPFormatted}
              onlyEVDealers={onlyEVDealersFormatted}
              preDefinedUserLocation={preDefinedUserLocation || undefined}
              preDefinedDealerData={data.dealerData?.dealer ? { dealer_code: data.dealerData.dealer.id || '', ...data.dealerData.dealer } : undefined}
              dealerId={dealerID}
              currentStep={currentStep}
              displayPrice={!isSelectionPreOrder()}
              startingAt={languageController.getCopy('global', 'pricePrefix') || ''}
            />
          </StepWrapper>
          {/* Step 2: Choose Date and Time */}
          <StepWrapper
            className={`mz-book-test-drive__date-and-time`}
            stepNum={2}
            currentStep={currentStep}
            currentProgress={currentProgress}
          >
            <DateAndTimeStep
              handleNextStep={handleNextStep}
              handlePreviousStep={handlePreviousStep}
              title={languageController.getCopy('global', 'selectDateAndTime') || ''}
              subTitle={languageController.getCopy('testDrive', 'step2Subtitle') || ''}
              isService={false}
            />
          </StepWrapper>
          {/* Step 3: Enter Contact Details */}
          <StepWrapper
            className={`mz-book-test-drive__contact-details`}
            stepNum={3}
            currentStep={currentStep}
            currentProgress={currentProgress}
          >
            <ContactDetailsStep
              handleNextStep={handleNextStep}
              title={languageController.getCopy('global', 'fillContactForm') || ''}
              submitButtonText={languageController.getCopy('testDrive', 'step4Submit') || ''}
              dealerData={data.dealerData as any}
              dateAndTimeData={data.dateAndTime ? { date: data.dateAndTime.date.toISOString().split('T')[0], time: data.dateAndTime.time } : undefined}
              vehicleData={data.vehicleData}
              formSource={source || ''}
              formPid={pidController.getPid('testDrive')}
              formEndPointUrl={getEndpointUrl('test-drive') || ''}
              formLanguage={convertLangCodeToSingleLetter(language as 'en' | 'fr' || 'en')}
              formDisclaimer={formDisclaimer || ''}
            />
          </StepWrapper>
          {/* Step 4: Show Confirmation */}
          <StepWrapper
            className={`mz-book-test-drive__confirmation`}
            stepNum={4}
            currentStep={currentStep}
            currentProgress={currentProgress}
          >
            <ConfirmationStep
              vehicleData={data.vehicleData as any}
              dateAndTime={data.dateAndTime ? { date: data.dateAndTime.date.toISOString().split('T')[0], time: data.dateAndTime.time } : undefined}
              dealerData={data.dealerData as any}
              contactDetails={data.contactDetails}
            />
          </StepWrapper>
        </div>
      </div>
    </>
  )
}

export default BookTestDrive
